import { Mail, Phone, User } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next'; // ADD THIS IMPORT
import FooterNavigation from '../../../shared/components/Navigation';
import { CustomerInfoType, NodeSettings, useCustomerInfo } from '../../../shared/hooks/useCustomerInfo';
import { SimpleNameInputs, SimpleTextInput } from './SimpleFormInputs';

export interface SimpleCustomerInfoProps {
  customerInfo: CustomerInfoType;
  setCustomerInfo: React.Dispatch<React.SetStateAction<CustomerInfoType>>;
  onNext: () => void;
  onPrev: () => void;
  nodeData?: {
    label: string;
    settings: NodeSettings;
  };
}

const SimpleCustomerInfo: React.FC<SimpleCustomerInfoProps> = ({
  customerInfo: initialCustomerInfo,
  setCustomerInfo: setParentCustomerInfo,
  onNext,
  onPrev,
  nodeData
}) => {
  const { t } = useTranslation(); // ADD THIS LINE

  // Use the custom hook
  const {
    customerInfo,
    errors,
    touched,
    handleChange,
    handlePhoneChange,
    handleBlur,
    isValid
  } = useCustomerInfo(initialCustomerInfo);

  // Sync local state with parent state
  React.useEffect(() => {
    setParentCustomerInfo(customerInfo);
  }, [customerInfo, setParentCustomerInfo]);

  // Default settings if nodeData is not provided
  const defaultSettings: NodeSettings = {
    requirePhone: true,
    requireEmail: true,
    showName: true,
    showLabels: true,
    showValidation: true,
    displayStyle: "simple",
    colors: {
      cardBackground: "#000000F2",
      cardBorder: "#DDB88919",
      cardBorderSelected: "#DDB889",
      cardShadow: "#DDB88919",
      cardShadowSelected: "#DDB88926",
      titleText: "#DDB889",
      subtitleText: "#DDB889B3",
      inputBackground: "#000000F2",
      inputBorder: "#DDB88933",
      inputText: "#DDB889",
      inputPlaceholder: "#DDB8894D",
      iconColor: "#DDB889B3",
      validationIconColor: "#22C55E",
      dividerColor: "#DDB88980",
      requiredFieldMark: "#F87171",
      selectionBackground: "#DDB889",
      selectionIcon: "#000000",
      badgeBackground: "#000000F2",
      badgeBorder: "#DDB88919",
      infoText: "#DDB889",
      buttonBackground: "#DDB889",
      buttonText: "#000000",
      placeholderGradientFrom: "#DDB88933",
      placeholderGradientTo: "#DDB8890D",
      placeholderIconBackground: "#DDB88933"
    }
  };

  // Use nodeData settings if available, otherwise use defaults
  const settings = nodeData?.settings || defaultSettings;
  const { colors } = settings;

  // Determine which fields to show based on settings
  const showName = settings.showName !== false;
  const showEmail = settings.requireEmail !== false;
  const showPhone = settings.requirePhone !== false;

  return (
    <div className="w-full transform scale-100">
      <div
        className="rounded-xl overflow-hidden p-4"
        style={{
          backgroundColor: colors.cardBackground,
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: colors.cardBorder
        }}
      >
        <div className="flex items-center mb-4">
          <User
            className="mr-2"
            size={20}
            style={{ color: colors.titleText }}
          />
          <h3 className="text-lg font-medium" style={{ color: colors.titleText }}>
            {nodeData?.label || t('booking.enterDetails')} {/* CHANGED: "Your Information" */}
          </h3>
        </div>

        <div className="space-y-3">
          {/* Name inputs */}
          {showName && (
            <SimpleNameInputs
              firstNameValue={customerInfo.firstName}
              lastNameValue={customerInfo.lastName}
              onChange={handleChange}
              onFirstNameBlur={() => handleBlur('firstName')}
              onLastNameBlur={() => handleBlur('lastName')}
              colors={colors}
            />
          )}

          {/* Email input */}
          {showEmail && (
            <SimpleTextInput
              name="email"
              placeholder={t('customerInfo.emailPlaceholder')} /* CHANGED: "Email" */
              value={customerInfo.email}
              onChange={handleChange}
              onBlur={() => handleBlur('email')}
              colors={colors}
              icon={Mail}
              type="email"
            />
          )}

          {/* Phone input */}
          {showPhone && (
            <SimpleTextInput
              name="phone"
              placeholder={t('customerInfo.phonePlaceholder')} /* CHANGED: "Phone Number" */
              value={customerInfo.phone}
              onChange={handleChange}
              onBlur={() => handleBlur('phone')}
              colors={colors}
              icon={Phone}
              type="tel"
            />
          )}
        </div>

        <div className="mt-4">
          <FooterNavigation
            onNext={onNext}
            onPrev={onPrev}
            disabled={!isValid}
          />
        </div>
      </div>
    </div>
  );
};

export default SimpleCustomerInfo;
